import { useCallback, useRef, type MutableRefObject } from "react";

interface PendingSave<TResult> {
  fingerprint: string;
  promise: Promise<TResult>;
}

interface CompletedSave<TResult> {
  fingerprint: string;
  result: TResult;
}

export function useDraftSaveCoordinator<TPayload, TResult>(
  saveDraft: (payload: TPayload) => Promise<TResult>
) {
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const pendingSaveCountRef = useRef(0);
  const latestQueuedSaveRef = useRef<PendingSave<TResult>>();
  const completedSaveRef = useRef<CompletedSave<TResult>>();

  return useCallback(
    (fingerprint: string, payload: TPayload): Promise<TResult> => {
      const latestQueuedSave = latestQueuedSaveRef.current;
      if (latestQueuedSave?.fingerprint === fingerprint) {
        return latestQueuedSave.promise;
      }
      if (
        pendingSaveCountRef.current === 0 &&
        completedSaveRef.current?.fingerprint === fingerprint
      ) {
        return Promise.resolve(completedSaveRef.current.result);
      }

      const pendingSave: PendingSave<TResult> = {
        fingerprint,
        promise: queueRef.current
          .catch(() => undefined)
          .then(() => saveDraft(payload)),
      };
      pendingSaveCountRef.current += 1;
      latestQueuedSaveRef.current = pendingSave;
      queueRef.current = pendingSave.promise.then(
        () => undefined,
        () => undefined
      );
      pendingSave.promise.then(
        (result) => {
          completedSaveRef.current = { fingerprint, result };
          settlePendingSave(
            pendingSaveCountRef,
            latestQueuedSaveRef,
            pendingSave
          );
        },
        () =>
          settlePendingSave(
            pendingSaveCountRef,
            latestQueuedSaveRef,
            pendingSave
          )
      );
      return pendingSave.promise;
    },
    [saveDraft]
  );
}

function settlePendingSave<TResult>(
  pendingSaveCountRef: MutableRefObject<number>,
  latestQueuedSaveRef: MutableRefObject<PendingSave<TResult> | undefined>,
  settledSave: PendingSave<TResult>
): void {
  pendingSaveCountRef.current -= 1;
  if (latestQueuedSaveRef.current?.promise === settledSave.promise) {
    latestQueuedSaveRef.current = undefined;
  }
}
